import { Injectable, computed, inject, signal } from '@angular/core';

import { firstValueFrom } from 'rxjs';

import { StorageManager } from '../../infrastructure/managers/storage.manager';
import { Client } from '../interfaces';
import { ClientService } from '../services/client.service';

@Injectable({ providedIn: 'root' })
export class ClientStore {
  private readonly clientService = inject(ClientService);
  private readonly storage = inject(StorageManager);

  readonly clients = signal<readonly Client[]>([]);
  readonly selectedClientUuids = signal<readonly string[]>([]);
  readonly loading = signal<boolean>(false);
  readonly error = signal<unknown>(null);

  readonly allSelected = computed(() =>
    this.clients().length > 0 && this.selectedClientUuids().length === this.clients().length,
  );

  readonly noneSelected = computed(() => this.selectedClientUuids().length === 0);
  readonly hasMultipleClients = computed(() => this.clients().length > 1);

  readonly selectedClients = computed(() => {
    const selectedUuidSet = new Set(this.selectedClientUuids());
    return this.clients().filter((client) => selectedUuidSet.has(client.uuid));
  });

  readonly selectionLabel = computed(() => {
    if (this.allSelected() || this.noneSelected()) {
      return 'All';
    }

    if (this.selectedClients().length === 1) {
      return this.selectedClients()[0].name;
    }

    return `${this.selectedClients().length} companies`;
  });

  async init(): Promise<void> {
    this.loading.set(true);
    this.error.set(null);

    try {
      const clients = await firstValueFrom(this.clientService.getMyClients());
      const persistedSelection = await this.storage.getClientSelection();
      const initialSelection = getInitialSelection(clients, persistedSelection);

      this.clients.set(clients);
      this.selectedClientUuids.set(initialSelection);
      await this.persistSelection();
    } catch (error: unknown) {
      this.clients.set([]);
      this.selectedClientUuids.set([]);
      this.error.set(error);
    } finally {
      this.loading.set(false);
    }
  }

  toggleClient(uuid: string): void {
    const availableUuidSet = new Set(this.clients().map((client) => client.uuid));
    if (!availableUuidSet.has(uuid)) {
      return;
    }

    const nextSelection = new Set(this.selectedClientUuids());
    if (nextSelection.has(uuid)) {
      nextSelection.delete(uuid);
    } else {
      nextSelection.add(uuid);
    }

    this.selectedClientUuids.set([...nextSelection]);
    void this.persistSelection();
  }

  selectAll(): void {
    this.selectedClientUuids.set(this.clients().map((client) => client.uuid));
    void this.persistSelection();
  }

  deselectAll(): void {
    this.selectedClientUuids.set([]);
    void this.persistSelection();
  }

  async clear(): Promise<void> {
    this.clients.set([]);
    this.selectedClientUuids.set([]);
    this.loading.set(false);
    this.error.set(null);
    await this.storage.clearClientSelection();
  }

  private async persistSelection(): Promise<void> {
    await this.storage.setClientSelection(this.selectedClientUuids());
  }
}

function getInitialSelection(
  clients: readonly Client[],
  persistedSelection: readonly string[] | null,
): string[] {
  if (clients.length === 0) {
    return [];
  }

  if (persistedSelection === null) {
    return clients.map((client) => client.uuid);
  }

  if (persistedSelection.length === 0) {
    return [];
  }

  const clientUuidSet = new Set(clients.map((client) => client.uuid));
  const validSelection = persistedSelection.filter((uuid) => clientUuidSet.has(uuid));

  if (validSelection.length === 0) {
    return clients.map((client) => client.uuid);
  }

  return [...validSelection];
}
